import { lazy, Suspense, useMemo, useState } from 'react'
import type { CmScope, CmSignalLink } from '@bloo-team/api-client'
import { caseSignalsGraph } from '../../lib/case-signal-graph'
import { sevKey } from '../../lib/case-mgmt'
import { readSignal, type SignalSeverity } from '../../lib/signal-model'
import { SIGNAL_ROWS_CAP, useSignalRows } from '../../lib/signals'
import { GraphErrorBoundary } from '../signal/force/GraphErrorBoundary'

/** The canvas chunk (force-graph) loads only when the graph shows — as Storylines. */
const loadForceGraph = () => import('../signal/force/StorylineForceGraph')
let ForceGraphView = lazy(loadForceGraph)
/** After a failed chunk: a fresh lazy component, so the import runs again. */
const retryForceGraph = () => {
  ForceGraphView = lazy(loadForceGraph)
}

const SEVERITIES: readonly SignalSeverity[] = ['critical', 'high', 'medium', 'low', 'info']
const severityOf = (s: string | null): SignalSeverity => {
  const k = sevKey(s) as SignalSeverity
  return SEVERITIES.includes(k) ? k : 'info'
}

/**
 * A case's signals on the Storylines canvas (docs/cases.md §Case page ·
 * Details): every attached signal a node, linked to its own suspects and
 * targets (lib/case-signal-graph.ts). Each signal's row is one read on the
 * signals service (the first SIGNAL_ROWS_CAP; CB-85), only while the graph
 * shows; a signal that cannot be read stays a node without entities.
 * Clicking a signal opens it on the Signals page. Props in, events out.
 */
export function CaseSignalsGraph({ scope, links, onOpen }: { scope: CmScope; links: readonly CmSignalLink[]; onOpen: (uuid: string) => void }) {
  const uuids = useMemo(() => links.map((l) => l.signalId), [links])
  const { ids, results, total } = useSignalRows(scope, uuids)
  const [attempt, setAttempt] = useState(0)
  const loading = results.some((r) => r.isLoading)
  const unread = loading ? 0 : results.filter((r) => r.isError || !r.data).length
  const byId = useMemo(() => new Map(links.map((l) => [l.signalId, l])), [links])
  // useQueries hands back a new array every render: rebuild the graph (and re-run the layout) only when a read changes.
  const readKey = results.map((r) => `${r.status}:${r.dataUpdatedAt}`).join('|')
  const graph = useMemo(
    () =>
      caseSignalsGraph(
        ids.map((uuid, i) => {
          const row = results[i]?.data
          const l = byId.get(uuid)
          return { uuid, title: l?.title ?? uuid, severity: severityOf(l?.severity ?? null), signal: row ? readSignal(row) : null }
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- readKey stands for results (see above)
    [ids, byId, readKey],
  )

  if (!links.length) return <p className="hud-unit">No signals attached.</p>
  if (loading) {
    return (
      <p className="hud-unit" role="status">
        Reading {ids.length} signals…
      </p>
    )
  }
  return (
    <div className="sg-graph">
      <GraphErrorBoundary
        key={attempt}
        onRetry={() => {
          retryForceGraph()
          setAttempt((a) => a + 1)
        }}
      >
        <Suspense
          fallback={
            <p className="hud-unit" role="status">
              Loading graph…
            </p>
          }
        >
          <ForceGraphView
            graph={graph}
            selected={null}
            onSelect={(id) => {
              if (id?.startsWith('sig:')) onOpen(id.slice(4))
            }}
            highlightEdgesOf={null}
            legend={[]}
            legendNote=""
            label="Signals graph — this case's signals with their suspects and targets; a signal opens on the Signals page"
          />
        </Suspense>
      </GraphErrorBoundary>
      <p className="hud-unit">
        {total > SIGNAL_ROWS_CAP ? `Showing the first ${SIGNAL_ROWS_CAP} of ${total}. ` : ''}
        {unread ? `${unread} of ${ids.length} signals could not be read — shown without their entities. ` : ''}
        Click a signal to open it on the Signals page.
      </p>
    </div>
  )
}
